import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  useMediaQuery,
  VStack,
  useStyleConfig,
} from '@chakra-ui/react';
import { useState, useEffect, useCallback } from 'react';
import BEIcon from '@molecules/BEIcon';
import { BEPopover, PopoverProps } from '@molecules/BEOffScreen/Popover';
import { usePopover } from '@molecules/BEOffScreen';
import Stepper from '@molecules/Stepper';
import ScreenSize from '../../../constants/screen-sizes';
import styled from '@emotion/styled';
import Text from '@molecules/Text';
import BEButton from '@molecules/Button/Button';

export interface GuestDropdownProps {
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  maxOccupancy: number;
  adults: number;
  kids: number;
  onChange?: (adults: number, children: number) => void;
}

export interface GuestSelectProps {
  target: 'Adults' | 'Children' | 'Infants';
  description: string;
  value?: number;
  isPlusDisabled: boolean;
  maxValue: number;
  minValue?: number;
  onChange?: (value: number) => void;
}

const GuestSelectStepper = ({
  target,
  description,
  value = 0,
  isPlusDisabled,
  minValue = 0,
  maxValue,
  onChange,
}: GuestSelectProps) => {
  return (
    <Box w="100%" paddingY="16px">
      <HStack w="100%" justifyContent="space-between">
        <Box>
          <Text type="h4">{target}</Text>
          <Text type="body">{description}</Text>
        </Box>
        <Stepper
          value={value}
          size="md"
          description=""
          plusDisabled={isPlusDisabled}
          minValue={minValue}
          maxValue={maxValue}
          typedInputDisabled={false}
          onChange={onChange}
        />
      </HStack>
    </Box>
  );
};

const StyledGuestDropdownContainer = styled(VStack)`
  width: 340px;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    width: 100%;
  }
`;

export const StyledCloseButton = styled.button`
  background-color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  border: none;

  &::selection {
      border: none;
  }
`;

const StyledMenuHeader = styled.div`
  display: none;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    display: flex;
    width: 100%;
    padding: 12px;
    justify-content: space-between;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
  }
`;

const StyledCloseWrapper = styled.div`
  margin-top: 16px;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    display: none;
  }
`;

const StyledMenuFooter = styled.div`
  display: none;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    display: flex;
    width: 100%;
    gap: 16px;
    padding: 16px 0;
    justify-content: space-between;
    align-items: center;
  }
`;

const GuestDropdownContent = ({
  maxOccupancy,
  adults,
  kids,
  onChange,
  ...popoverProps
}: GuestDropdownProps & PopoverProps) => {
  const [_adults, setAdults] = useState(adults);
  const [_kids, setKids] = useState(kids);
  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);

  const maxOccupancyReached = _adults + _kids >= maxOccupancy;

  useEffect(() => {
    if (!isMobile) {
      onChange?.(_adults, _kids);
    }
  }, [_adults, _kids, maxOccupancy, onChange]);

  const handleApply = () => {
    onChange?.(_adults, _kids);
    popoverProps.onCancel && popoverProps.onCancel();
  };

  const onChangeAdults = useCallback(
    (value: number) => {
      if (value >= 1) setAdults(value);
    },
    [setAdults]
  );

  return (
    <BEPopover {...popoverProps}>
      <StyledGuestDropdownContainer spacing={0}>
        <StyledMenuHeader>
          Select Guests
          <StyledCloseButton onClick={popoverProps.onCancel}>
            <BEIcon size="lg" name="close" weight="solid" />
          </StyledCloseButton>
        </StyledMenuHeader>
        <Divider />
        <VStack w="100%" spacing={0} paddingX="16px">
          <GuestSelectStepper
            target="Adults"
            description="Age 18+"
            value={_adults}
            minValue={1}
            maxValue={maxOccupancy - _kids}
            isPlusDisabled={maxOccupancyReached}
            onChange={onChangeAdults}
          />
          <Divider />
          <GuestSelectStepper
            target="Children"
            description="Age 13-18"
            value={_kids}
            maxValue={maxOccupancy - _adults}
            isPlusDisabled={maxOccupancyReached}
            onChange={setKids}
          />
          <Divider />
          <Flex
            w="100%"
            alignContent="flex-start"
            paddingY="16px"
            flexDirection="column"
          >
            <Text type="body">Max occupancy - {maxOccupancy} guests</Text>
            <StyledCloseWrapper>
              <BEButton
                color="neutral"
                variant="outline"
                size="md"
                onClick={popoverProps.onCancel}
              >
                Close
              </BEButton>
            </StyledCloseWrapper>
          </Flex>
          <Divider />
          <StyledMenuFooter>
            <BEButton
              color="neutral"
              variant="outline"
              width="100%"
              size="md"
              onClick={popoverProps.onCancel}
            >
              Cancel
            </BEButton>
            <BEButton
              color="primary"
              width="100%"
              size="md"
              onClick={handleApply}
            >
              Apply
            </BEButton>
          </StyledMenuFooter>
        </VStack>
      </StyledGuestDropdownContainer>
    </BEPopover>
  );
};

export const GuestDropdown = ({
  size = 'sm',
  label,
  maxOccupancy,
  adults,
  kids,
  onChange,
}: GuestDropdownProps) => {
  const [_adults, setAdults] = useState(adults);
  const [_kids, setKids] = useState(kids);
  const [toggleRef, setToggleRef] = useState<any>();

  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);

  const popover = usePopover(GuestDropdownContent, {
    id: `guest_dropdown_${maxOccupancy}`,
    referenceElement: toggleRef,
    renderMethod: isMobile ? 'fullscreen' : 'reference',
    zIndex: 9999,
    ...(isMobile && { pinTo: 'bottom', hasOverlay: true }),
  });
  const isOpen = popover.isOpened();

  const handleChange = (_adults: number, _kids: number) => {
    setAdults(_adults);
    setKids(_kids);
    onChange?.(_adults, _kids);
  };

  const handleOpenPopover = () => {
    if (isOpen) {
      popover.close();
    } else {
      popover.open({
        maxOccupancy,
        adults: _adults,
        kids: _kids,
        onChange: handleChange,
      });
    }
  };

  const styles = useStyleConfig('GuestDropdown', { size });

  return (
    <VStack display="inline-block" spacing="4px">
      {label && (
        <Text
          type="card-body"
          mobilesize={size === 'sm' ? 'xs' : size === 'md' ? 'sm' : 'md'}
          size={size === 'sm' ? 'xs' : size === 'md' ? 'sm' : 'md'}
        >
          {label}
        </Text>
      )}
      <Button
        rightIcon={
          <BEIcon name={isOpen ? 'angle-up' : 'angle-down'} size="sm" />
        }
        onClick={handleOpenPopover}
        ref={setToggleRef}
        __css={styles}
      >
        {_adults + _kids}
      </Button>
    </VStack>
  );
};

export default GuestDropdown;
